import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  getLrsAssetTypeMeta,
  getUtilityTypeMeta,
  LRS_UTILITY_TYPES,
} from 'src/app/shared/constants/lrs-asset-types';
import {
  LrsDashboardService,
  LrsQuickAction,
} from 'src/app/shared/services/lrs-dashboard.service';
import {
  LrsCrossSection,
  LrsCrossSectionAsset,
  LrsRoadStrip,
  LrsRouteKpis,
  LrsService,
  LrsVerticalProfile,
  LrsVerticalPvi,
  LrsVerticalSample,
} from 'src/app/shared/services/lrs.service';
import { formatChainageFromMeters } from 'src/app/shared/utils/chainage.util';
import { Subscription } from 'rxjs';

type GradeTab = 'underground' | 'overground' | 'crossover';
type LayerKey =
  | 'pavement'
  | 'shoulder'
  | 'median'
  | 'drainage'
  | 'electric'
  | 'telecom'
  | 'water'
  | 'gas'
  | 'sewer'
  | 'structures'
  | 'ground';

interface DonutSlice {
  id: string;
  label: string;
  count: number;
  color: string;
  dash: string;
  offset: number;
}

interface DepthBar {
  id: string;
  label: string;
  depth: number;
  color: string;
}

@Component({
  selector: 'app-lrs-dashboard-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './lrs-dashboard-panel.component.html',
  styleUrls: ['./lrs-dashboard-panel.component.css'],
})
export class LrsDashboardPanelComponent implements OnInit, OnDestroy {
  @Input() expanded = false;
  @Output() expandedChange = new EventEmitter<boolean>();

  @HostBinding('class.lrs-dash-host--expanded')
  get hostExpanded(): boolean {
    return this.expanded;
  }

  @HostBinding('class.lrs-dash-host--collapsed')
  get hostCollapsed(): boolean {
    return !this.expanded;
  }

  kpis: LrsRouteKpis | null = null;
  section: LrsCrossSection | null = null;
  profile: LrsVerticalProfile | null = null;
  gradeTab: GradeTab = 'underground';
  loadingKpis = false;
  loadingSection = false;
  loadingProfile = false;
  profileFailed = false;
  stepIntervalM = 25;
  layersOpen = false;
  showIso = false;
  layers: Record<LayerKey, boolean> = {
    pavement: true,
    shoulder: true,
    median: true,
    drainage: true,
    electric: true,
    telecom: true,
    water: true,
    gas: true,
    sewer: true,
    structures: true,
    ground: true,
  };

  readonly layerGroups: { label: string; items: { id: LayerKey; label: string }[] }[] = [
    {
      label: 'Pavement',
      items: [
        { id: 'pavement', label: 'Pavement' },
        { id: 'shoulder', label: 'Shoulder' },
        { id: 'median', label: 'Median' },
        { id: 'drainage', label: 'Drainage' },
        { id: 'ground', label: 'Existing ground' },
      ],
    },
    {
      label: 'Utilities',
      items: [
        { id: 'electric', label: 'Electric' },
        { id: 'telecom', label: 'Telecom' },
        { id: 'water', label: 'Water' },
        { id: 'gas', label: 'Gas' },
        { id: 'sewer', label: 'Sewer' },
      ],
    },
    {
      label: 'Structures',
      items: [{ id: 'structures', label: 'Culverts / structures' }],
    },
  ];

  readonly actions: { id: LrsQuickAction; label: string }[] = [
    { id: 'add-asset', label: 'Add Asset' },
    { id: 'add-utility', label: 'Add Utility Line' },
    { id: 'add-structure', label: 'Add Structure' },
    { id: 'draw-corridor', label: 'Draw Corridor' },
    { id: 'upload-file', label: 'Upload File' },
    { id: 'take-photo', label: 'Take Photo' },
    { id: 'generate-report', label: 'Generate Report' },
    { id: 'export-data', label: 'Export Data' },
  ];

  readonly gradeTabs: { id: GradeTab; label: string; title: string }[] = [
    { id: 'underground', label: 'UG', title: 'Underground' },
    { id: 'overground', label: 'OG', title: 'Overground' },
    { id: 'crossover', label: 'XO', title: 'Crossing' },
  ];

  private subs: Subscription[] = [];
  private sectionTimer: ReturnType<typeof setTimeout> | null = null;
  private profileTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private dashboard: LrsDashboardService,
    private lrs: LrsService
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.dashboard.routeId$.subscribe(() => {
        this.loadKpis();
        this.scheduleSection();
        this.scheduleProfile();
      }),
      this.dashboard.kpisDirty$.subscribe(() => {
        this.loadKpis();
        this.scheduleProfile(200);
      }),
      this.dashboard.selectedAsset$.subscribe(() => this.scheduleSection()),
      this.dashboard.chainage$.subscribe(() => {
        this.scheduleSection(400);
        this.scheduleProfile(400);
      }),
      this.dashboard.kpis$.subscribe((k) => (this.kpis = k)),
      this.dashboard.crossSection$.subscribe((s) => (this.section = s)),
      this.dashboard.verticalProfile$.subscribe((p) => (this.profile = p))
    );
    this.loadKpis();
    this.scheduleSection();
    this.scheduleProfile();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    if (this.sectionTimer) clearTimeout(this.sectionTimer);
    if (this.profileTimer) clearTimeout(this.profileTimer);
  }

  setExpanded(next: boolean): void {
    if (this.expanded === next) return;
    this.expanded = next;
    this.expandedChange.emit(next);
  }

  toggleExpanded(): void {
    this.expandedChange.emit(!this.expanded);
  }

  runAction(id: LrsQuickAction): void {
    if (id === 'take-photo' || id === 'upload-file') {
      this.dashboard.requestMedia(id === 'take-photo' ? 'photo' : 'file');
    }
    this.dashboard.requestAction(id);
  }

  setGradeTab(tab: GradeTab): void {
    this.gradeTab = tab;
  }

  openAlignment(): void {
    this.dashboard.requestAction('open-alignment');
  }

  get hasVa(): boolean {
    return !this.profileFailed && this.vaPvis.length > 0 && !!this.vaPathDesign;
  }

  toggleLayer(id: LayerKey): void {
    this.layers[id] = !this.layers[id];
  }

  get total(): number {
    return this.kpis?.total || 0;
  }

  get ugCount(): number {
    return this.kpis?.by_grade.underground || 0;
  }

  get ogCount(): number {
    return this.kpis?.by_grade.overground || 0;
  }

  get xoCount(): number {
    return this.kpis?.by_grade.crossover || 0;
  }

  get typeSlices(): DonutSlice[] {
    return this.buildDonut(this.typeEntries);
  }

  get typeLegendSlices(): DonutSlice[] {
    return this.typeSlices.slice(0, 5);
  }

  get statusSlices(): DonutSlice[] {
    const life = this.kpis?.by_lifecycle;
    if (!life) return [];
    return this.buildDonut([
      { id: 'operational', label: 'Operational', count: life.operational, color: '#16a34a' },
      { id: 'construction', label: 'Under Construction', count: life.construction, color: '#f59e0b' },
      { id: 'planned', label: 'Planned', count: life.planned, color: '#38bdf8' },
      { id: 'decommissioned', label: 'Decommissioned', count: life.decommissioned, color: '#94a3b8' },
    ]);
  }

  get visibleAssets(): LrsCrossSectionAsset[] {
    const assets = this.section?.assets || [];
    return assets.filter((a) => this.normalizeGrade(a.grade) === this.gradeTab && this.layerAllows(a));
  }

  get chainageLabel(): string {
    const raw =
      this.dashboard.getChainage()?.label ||
      this.section?.chainage_label ||
      this.profile?.cursor?.chainage_label ||
      '—';
    return this.toKmLabel(raw);
  }

  get stationMeasure(): number {
    return (
      this.dashboard.getChainage()?.measure_m ??
      this.section?.measure_m ??
      this.profile?.cursor?.measure_m ??
      this.routeOrigin
    );
  }

  get routeOrigin(): number {
    return this.dashboard.getRouteMeta()?.origin_m ?? this.profile?.origin_m ?? this.section?.origin_m ?? 0;
  }

  get routeEnd(): number {
    const origin = this.routeOrigin;
    const len = this.dashboard.getRouteMeta()?.length_m ?? this.profile?.length_m ?? this.section?.length_m ?? 0;
    return origin + Math.max(len, 0);
  }

  get roadLanes(): LrsRoadStrip[] {
    return this.section?.road?.lanes || [];
  }

  get roadShoulders(): LrsRoadStrip[] {
    return this.section?.road?.shoulders || [];
  }

  get roadMedian(): LrsRoadStrip | null {
    return this.section?.road?.median || null;
  }

  get depthBars(): DepthBar[] {
    const byType = new Map<string, { depth: number; color: string; label: string }>();
    for (const a of this.section?.assets || []) {
      if (this.normalizeGrade(a.grade) !== 'underground') continue;
      const key = a.type || a.kind;
      const prev = byType.get(key);
      const depth = Math.abs(Number(a.depth_m) || 0);
      const color = this.colorFor(a);
      const label = this.shortLabel(a);
      if (!prev || depth > prev.depth) byType.set(key, { depth, color, label });
    }
    return [...byType.entries()]
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.depth - a.depth)
      .slice(0, 8);
  }

  get maxDepth(): number {
    return Math.max(1, ...this.depthBars.map((b) => b.depth));
  }

  get vaPathDesign(): string {
    return this.polyline(this.profile?.samples || [], 'design_elev_m');
  }

  get vaPathGround(): string {
    return this.polyline(this.profile?.samples || [], 'ground_elev_m');
  }

  get vaFill(): string {
    const samples = this.profile?.samples || [];
    const design = samples.filter((s) => s.design_elev_m != null);
    const ground = [...samples].reverse().filter((s) => s.ground_elev_m != null);
    if (design.length < 2 || ground.length < 2) return '';
    const pts = [
      ...design.map((s) => `${this.vaX(s.measure_m)},${this.vaY(Number(s.design_elev_m))}`),
      ...ground.map((s) => `${this.vaX(s.measure_m)},${this.vaY(Number(s.ground_elev_m))}`),
    ];
    return `M ${pts.join(' L ')} Z`;
  }

  get vaPvis(): LrsVerticalPvi[] {
    return this.profile?.pvis || [];
  }

  get miniSamples(): LrsVerticalSample[] {
    const samples = this.profile?.samples || [];
    const m = this.stationMeasure;
    return samples.filter((s) => Math.abs(s.measure_m - m) <= 100);
  }

  get miniPathDesign(): string {
    return this.miniPolyline(this.miniSamples, 'design_elev_m');
  }

  get miniPathGround(): string {
    return this.miniPolyline(this.miniSamples, 'ground_elev_m');
  }

  get offsetTicks(): number[] {
    const left = this.section?.road?.row_left_m ?? this.section?.pavement_left_m ?? -12;
    const right = this.section?.road?.row_right_m ?? this.section?.pavement_right_m ?? 12;
    const span = Math.max(8, right - left);
    const step = span > 30 ? 10 : 5;
    const ticks: number[] = [];
    const start = Math.ceil(left / step) * step;
    for (let t = start; t <= right + 0.01; t += step) ticks.push(t);
    if (!ticks.includes(0) && left < 0 && right > 0) ticks.push(0);
    return ticks.sort((a, b) => a - b);
  }

  xForOffset(offset: number): number {
    const left = this.section?.road?.row_left_m ?? -18;
    const right = this.section?.road?.row_right_m ?? 18;
    const min = Math.min(left, -8);
    const max = Math.max(right, 8);
    const clamped = Math.max(min, Math.min(max, offset));
    return 36 + ((clamped - min) / (max - min || 1)) * 360;
  }

  yForDepth(depth: number, grade?: string): number {
    const g = this.normalizeGrade(grade);
    if (g === 'overground') {
      return 44 - Math.min(22, Math.abs(depth) * 4);
    }
    if (g === 'crossover') {
      return 62;
    }
    return 88 + Math.min(50, Math.abs(depth) * 18);
  }

  radiusFor(asset: LrsCrossSectionAsset): number {
    const mm = Number(asset.diameter_mm);
    if (Number.isFinite(mm) && mm > 0) return Math.max(6, Math.min(16, mm / 60));
    return asset.kind === 'utility' ? 8 : 7;
  }

  colorFor(asset: LrsCrossSectionAsset): string {
    if (asset.kind === 'utility') {
      return getUtilityTypeMeta(asset.type)?.color || '#0284c7';
    }
    return getLrsAssetTypeMeta(asset.type || '')?.color || '#64748b';
  }

  labelFor(asset: LrsCrossSectionAsset): string {
    return this.shortLabel(asset);
  }

  pavementLeftX(): number {
    return this.xForOffset(this.section?.pavement_left_m ?? -3.5);
  }

  pavementRightX(): number {
    return this.xForOffset(this.section?.pavement_right_m ?? 3.5);
  }

  isoX(offset: number, depth: number): number {
    return 90 + offset * 3.2 + Math.abs(depth) * 2;
  }

  isoY(offset: number, depth: number, grade?: string): number {
    const g = this.normalizeGrade(grade);
    const surface = 70 + offset * 0.55;
    if (g === 'overground') return surface - 16;
    if (g === 'crossover') return surface - 4;
    return surface + 12 + Math.abs(depth) * 10;
  }

  vaX(measureM: number): number {
    const from = this.profile?.from_m ?? this.routeOrigin;
    const to = (this.profile?.to_m ?? this.routeEnd) || from + 1;
    return 36 + ((measureM - from) / (to - from || 1)) * 340;
  }

  vaY(elev: number): number {
    const samples = this.profile?.samples || [];
    const vals = samples.flatMap((s) => [s.design_elev_m, s.ground_elev_m]).filter((v): v is number => v != null);
    const min = vals.length ? Math.min(...vals) - 1 : 400;
    const max = vals.length ? Math.max(...vals) + 1 : 440;
    return 148 - ((elev - min) / (max - min || 1)) * 112;
  }

  miniX(measureM: number): number {
    const m = this.stationMeasure;
    const from = m - 100;
    const to = m + 100;
    return 8 + ((measureM - from) / (to - from || 1)) * 164;
  }

  miniY(elev: number): number {
    const vals = this.miniSamples
      .flatMap((s) => [s.design_elev_m, s.ground_elev_m])
      .filter((v): v is number => v != null);
    const min = vals.length ? Math.min(...vals) - 0.4 : 400;
    const max = vals.length ? Math.max(...vals) + 0.4 : 440;
    return 34 - ((elev - min) / (max - min || 1)) * 26;
  }

  get vaTickMarks(): { m: number; label: string }[] {
    const from = this.profile?.from_m ?? this.routeOrigin;
    const to = this.profile?.to_m ?? this.routeEnd;
    const span = Math.max(1, to - from);
    const step = span > 4000 ? 1000 : span > 1500 ? 500 : 250;
    const ticks: { m: number; label: string }[] = [];
    const start = Math.ceil(from / step) * step;
    for (let m = start; m <= to + 0.01; m += step) {
      ticks.push({ m, label: this.toKmLabel(formatChainageFromMeters(m)) });
    }
    return ticks;
  }

  stepFirst(): void {
    this.goToMeasure(this.routeOrigin);
  }

  stepLast(): void {
    this.goToMeasure(this.routeEnd);
  }

  stepPrev(): void {
    this.goToMeasure(this.stationMeasure - Math.max(5, Number(this.stepIntervalM) || 25));
  }

  stepNext(): void {
    this.goToMeasure(this.stationMeasure + Math.max(5, Number(this.stepIntervalM) || 25));
  }

  private goToMeasure(measureM: number): void {
    const clamped = Math.max(this.routeOrigin, Math.min(this.routeEnd || measureM, measureM));
    const label = this.toKmLabel(formatChainageFromMeters(clamped));
    this.dashboard.selectAsset(null);
    this.dashboard.seekStation({ measure_m: clamped, label });
  }

  private toKmLabel(label: string): string {
    return String(label || '').replace(/^CH\s*/i, 'KM ');
  }

  private polyline(samples: LrsVerticalSample[], key: 'design_elev_m' | 'ground_elev_m'): string {
    const pts = samples.filter((s) => s[key] != null);
    if (pts.length < 2) return '';
    return pts.map((s, i) => `${i ? 'L' : 'M'} ${this.vaX(s.measure_m)} ${this.vaY(Number(s[key]))}`).join(' ');
  }

  private miniPolyline(samples: LrsVerticalSample[], key: 'design_elev_m' | 'ground_elev_m'): string {
    const pts = samples.filter((s) => s[key] != null);
    if (pts.length < 2) return '';
    return pts.map((s, i) => `${i ? 'L' : 'M'} ${this.miniX(s.measure_m)} ${this.miniY(Number(s[key]))}`).join(' ');
  }

  private shortLabel(asset: LrsCrossSectionAsset): string {
    if (asset.label) return String(asset.label);
    if (asset.kind === 'utility') {
      return getUtilityTypeMeta(asset.type)?.label || asset.type || 'Utility';
    }
    return getLrsAssetTypeMeta(asset.type || '')?.label || asset.type || 'Asset';
  }

  private layerAllows(asset: LrsCrossSectionAsset): boolean {
    const t = String(asset.type || '').toLowerCase();
    if (t.includes('drain') || t.includes('culvert')) return this.layers.drainage || this.layers.structures;
    if (t.includes('electric') || t.includes('ht') || t.includes('lt') || t.includes('lighting')) {
      return this.layers.electric;
    }
    if (t.includes('ofc') || t.includes('telecom') || t.includes('fiber')) return this.layers.telecom;
    if (t.includes('water')) return this.layers.water;
    if (t.includes('gas')) return this.layers.gas;
    if (t.includes('sewer')) return this.layers.sewer;
    if (t.includes('bridge') || t.includes('underpass') || t.includes('overpass')) return this.layers.structures;
    if (asset.kind === 'utility') return true;
    return this.layers.structures || this.layers.pavement;
  }

  private get typeEntries(): { id: string; label: string; count: number; color: string }[] {
    const byType = this.kpis?.by_type || {};
    return Object.entries(byType)
      .map(([id, count]) => {
        const util = LRS_UTILITY_TYPES.find((u) => u.id === id);
        const hw = getLrsAssetTypeMeta(id);
        return {
          id,
          label: util?.label || hw?.label || id,
          count,
          color: util?.color || hw?.color || '#64748b',
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }

  private buildDonut(
    entries: { id: string; label: string; count: number; color: string }[]
  ): DonutSlice[] {
    const total = entries.reduce((s, e) => s + e.count, 0) || 1;
    const C = 2 * Math.PI * 36;
    let acc = 0;
    return entries
      .filter((e) => e.count > 0)
      .map((e) => {
        const frac = e.count / total;
        const dash = `${frac * C} ${C}`;
        const offset = -acc * C;
        acc += frac;
        return { ...e, dash, offset };
      });
  }

  private normalizeGrade(grade?: string): GradeTab {
    const g = String(grade || '').toLowerCase();
    if (g === 'underground' || g === 'ug') return 'underground';
    if (g === 'crossover' || g === 'crossing') return 'crossover';
    return 'overground';
  }

  private loadKpis(): void {
    const routeId = this.dashboard.getRouteId();
    if (!routeId) {
      this.dashboard.setKpis(null);
      return;
    }
    this.loadingKpis = true;
    this.lrs.getRouteKpis(routeId).subscribe({
      next: (res) => {
        this.loadingKpis = false;
        this.dashboard.setKpis(res.data);
      },
      error: () => {
        this.loadingKpis = false;
      },
    });
  }

  private scheduleSection(delay = 120): void {
    if (this.sectionTimer) clearTimeout(this.sectionTimer);
    this.sectionTimer = setTimeout(() => this.loadSection(), delay);
  }

  private scheduleProfile(delay = 160): void {
    if (this.profileTimer) clearTimeout(this.profileTimer);
    this.profileTimer = setTimeout(() => this.loadProfile(), delay);
  }

  private loadSection(): void {
    const routeId = this.dashboard.getRouteId();
    if (!routeId) {
      this.dashboard.setCrossSection(null);
      return;
    }
    const hud = this.dashboard.getChainage();
    const selected = this.dashboard.getSelectedAsset();
    const opts =
      hud != null
        ? { measure_m: hud.measure_m }
        : selected?.measure_m != null
          ? { measure_m: selected.measure_m }
          : undefined;
    this.loadingSection = true;
    this.lrs.getCrossSection(routeId, opts).subscribe({
      next: (res) => {
        this.loadingSection = false;
        this.dashboard.setCrossSection(res.data);
        if (res.data?.origin_m != null && res.data?.length_m != null) {
          this.dashboard.setRouteMeta({ origin_m: res.data.origin_m, length_m: res.data.length_m });
        }
      },
      error: () => {
        this.loadingSection = false;
      },
    });
  }

  private loadProfile(): void {
    const routeId = this.dashboard.getRouteId();
    if (!routeId) {
      this.dashboard.setVerticalProfile(null);
      return;
    }
    const hud = this.dashboard.getChainage();
    this.loadingProfile = true;
    this.lrs.getVerticalProfile(routeId, { step_m: 25, measure_m: hud?.measure_m }).subscribe({
      next: (res) => {
        this.loadingProfile = false;
        this.profileFailed = false;
        this.dashboard.setVerticalProfile(res.data);
      },
      error: () => {
        this.loadingProfile = false;
        this.profileFailed = true;
        this.dashboard.setVerticalProfile(null);
      },
    });
  }
}
